/** Line chart for the score trend and the payoff schedule. */
import { chart } from '@/lib/tokens'
import { t } from '@/lib/copy'

export interface LinePoint {
  label: string
  value: number
}

export function LineChart({
  points,
  color = 'var(--color-primary)',
  height = 200,
  formatValue,
  caption,
  fromZero = false,
}: {
  points: LinePoint[]
  color?: string
  height?: number
  formatValue: (value: number) => string
  caption: string
  fromZero?: boolean
}) {
  if (points.length === 0) return null

  const width = 720
  const padBottom = 24
  const padTop = 8
  const plotHeight = height - padBottom - padTop
  const values = points.map((p) => p.value)
  const max = Math.max(...values, fromZero ? 0 : -Infinity)
  const min = fromZero ? Math.min(0, ...values) : Math.min(...values)
  const span = max - min || 1
  const step = points.length > 1 ? width / (points.length - 1) : 0

  const x = (i: number) => (points.length > 1 ? i * step : width / 2)
  const y = (value: number) => padTop + plotHeight - ((value - min) / span) * plotHeight

  const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(p.value).toFixed(1)}`).join(' ')
  const area = `${path} L${x(points.length - 1).toFixed(1)},${padTop + plotHeight} L${x(0).toFixed(1)},${padTop + plotHeight} Z`

  const labelEvery = Math.max(1, Math.ceil(points.length / 8))

  return (
    <figure style={{ margin: 0 }}>
      <svg className="chart" viewBox={`0 0 ${width} ${height}`} style={{ height }} role="img" aria-label={caption}>
        {[0, 0.5, 1].map((line) => (
          <line
            key={line}
            x1={0}
            x2={width}
            y1={padTop + plotHeight - line * plotHeight}
            y2={padTop + plotHeight - line * plotHeight}
            stroke="var(--color-chartGrid)"
            strokeDasharray={chart.gridDashArray}
          />
        ))}
        <path d={area} fill={color} opacity="0.12" />
        <path d={path} fill="none" stroke={color} strokeWidth={chart.lineWidth} strokeLinejoin="round" />
        {/* Keyed by index: labels repeat once a series crosses a year boundary. */}
        {points.map((point, i) => (
          <circle key={i} cx={x(i)} cy={y(point.value)} r={chart.dotRadius} fill={color}>
            <title>{t('a11y.chartBar', { label: point.label, amount: formatValue(point.value) })}</title>
          </circle>
        ))}
        {points.map((point, i) =>
          i % labelEvery === 0 ? (
            <text
              key={`label-${i}`}
              x={x(i)}
              y={height - 6}
              textAnchor="middle"
              fill="var(--color-textTertiary)"
              fontSize="12"
            >
              {point.label}
            </text>
          ) : null,
        )}
      </svg>
      <figcaption className="visually-hidden">
        {points.map((p) => t('a11y.chartBar', { label: p.label, amount: formatValue(p.value) })).join('. ')}
      </figcaption>
    </figure>
  )
}
